import React, { useState, useEffect } from "react";

import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import { toast } from "react-toastify";

import clearNight from "../assets/clear_night.json";
import cloudy from "../assets/cloudy.json";
import fewClouds from "../assets/few_clouds_new.json";
import fog from "../assets/fog.json";
import partlyCloudy from "../assets/partly_cloudy_new.json";
import partlyCloudyNight from "../assets/partly_cloudy_night.json";
import rain from "../assets/rain.json";
import rainNight from "../assets/rain_night.json";
import snow from "../assets/snow.json";
import snowNight from "../assets/snow_night.json";
import storm from "../assets/storm.json";
import stormNight from "../assets/storm_night.json";
import sunny from "../assets/sunny.json";
import { debugLog } from "../utils/debugLog";
import { activateOnEnterOrSpace } from "../utils/keyboardActivation";
import { fetchWeatherAndSetBackground } from "../utils/weatherBackground";

import "./WeatherWidget.css";

// Open-Meteo (WMO) weather code → human description. Shared by current + forecast.
const weatherDescriptions = {
  0: "Clear Sky",
  1: "Mainly Clear",
  2: "Partly Cloudy",
  3: "Overcast",
  45: "Foggy",
  48: "Rime Fog",
  51: "Light Drizzle",
  53: "Moderate Drizzle",
  55: "Dense Drizzle",
  61: "Light Rain",
  63: "Moderate Rain",
  65: "Heavy Rain",
  71: "Light Snow",
  73: "Moderate Snow",
  75: "Heavy Snow",
  77: "Snow Grains",
  80: "Light Showers",
  81: "Moderate Showers",
  82: "Heavy Showers",
  85: "Snow Showers",
  86: "Heavy Snow Showers",
  95: "Thunderstorm",
  96: "Thunderstorm with Hail",
  99: "Severe Thunderstorm",
};

const weatherUnit = () => {
  const region = (navigator.language || "en-US").split("-")[1]?.toUpperCase();

  return region === "US" ? "fahrenheit" : "celsius";
};

const weatherUnitSymbol = () => (weatherUnit() === "fahrenheit" ? "°F" : "°C");

// Highest hourly US AQI per local day, keyed by ISO date. Missing data is fine.
const fetchDailyAqi = async (lat, lon) => {
  try {
    const res = await fetch(
      `https://air-quality-api.open-meteo.com/v1/air-quality?latitude=${lat}&longitude=${lon}` +
        `&hourly=us_aqi&timezone=auto&forecast_days=5`
    );

    if (!res.ok) return {};

    const { hourly } = await res.json();
    const byDay = {};

    (hourly?.time || []).forEach((time, i) => {
      const value = hourly.us_aqi?.[i];

      if (value == null) return;
      const day = time.slice(0, 10);

      byDay[day] = Math.max(byDay[day] ?? 0, Math.round(value));
    });

    return byDay;
  } catch {
    return {};
  }
};

const WeatherWidget = ({ compact = false }) => {
  const [weatherData, setWeatherData] = useState(null);
  const [clock, setClock] = useState(() =>
    new Date().toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
  );
  const [showWeatherPopup, setShowWeatherPopup] = useState(false);
  const [locationInput, setLocationInput] = useState("");
  const [forecast, setForecast] = useState([]);
  const [showLocationForm, setShowLocationForm] = useState(false);

  // Function to get weather animation based on weather code and time
  const getWeatherAnimation = (weatherCode, isNight = false) => {
    const code = String(weatherCode).padStart(2, "0");

    // Map weather codes to animations (based on Android app logic)
    if (code.startsWith("01")) {
      return isNight ? clearNight : sunny;
    } else if (code.startsWith("02")) {
      return isNight ? partlyCloudyNight : partlyCloudy;
    } else if (code.startsWith("03")) {
      return isNight ? partlyCloudyNight : fewClouds;
    } else if (code.startsWith("04")) {
      return cloudy;
    } else if (code.startsWith("09") || code.startsWith("10")) {
      return isNight ? rainNight : rain;
    } else if (code.startsWith("11")) {
      return isNight ? stormNight : storm;
    } else if (code.startsWith("13")) {
      return isNight ? snowNight : snow;
    } else if (code.startsWith("50")) {
      return fog;
    }

    // For Open-Meteo weather codes (different from OpenWeatherMap)
    switch (weatherCode) {
      case 0:
      case 1:
        return isNight ? clearNight : sunny;
      case 2:
        return isNight ? partlyCloudyNight : partlyCloudy;
      case 3:
        return cloudy;
      case 45:
      case 48:
        return fog;
      case 51:
      case 53:
      case 55:
      case 61:
      case 63:
      case 65:
      case 80:
      case 81:
      case 82:
        return isNight ? rainNight : rain;
      case 71:
      case 73:
      case 75:
      case 77:
      case 85:
      case 86:
        return isNight ? snowNight : snow;
      case 95:
      case 96:
      case 99:
        return isNight ? stormNight : storm;
      default:
        return isNight ? clearNight : sunny;
    }
  };

  // Check if it's nighttime based on current hour
  const isNightTime = () => {
    const hour = new Date().getHours();

    return hour < 6 || hour > 20;
  };

  // Fetch a 5-day daily forecast from Open-Meteo and map it to display rows
  // (reuses the same Lottie icon mapping as the current-conditions icon).
  const fetchForecast = async (lat, lon) => {
    try {
      const [res, aqiByDay] = await Promise.all([
        fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
            `&daily=weathercode,temperature_2m_max,temperature_2m_min` +
            `&temperature_unit=${weatherUnit()}&timezone=auto&forecast_days=5`
        ),
        fetchDailyAqi(lat, lon),
      ]);

      if (!res.ok) return [];

      const data = await res.json();
      const daily = data?.daily;

      if (!daily?.time?.length) return [];

      return daily.time.map((iso, i) => {
        const date = new Date(`${iso}T00:00:00`);

        return {
          label:
            i === 0
              ? "Today"
              : date.toLocaleDateString("en-US", { weekday: "short" }),
          date: date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          animation: getWeatherAnimation(daily.weathercode[i], false),
          condition: weatherDescriptions[daily.weathercode[i]] || "—",
          hi: Math.round(daily.temperature_2m_max[i]),
          lo: Math.round(daily.temperature_2m_min[i]),
          aqi: aqiByDay[iso],
        };
      });
    } catch (error) {
      console.error("WeatherWidget: Error fetching forecast:", error);

      return [];
    }
  };

  // Fetch weather data using the same utility as login page
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        debugLog("WeatherWidget: Starting weather fetch...");

        // Check for saved location first
        const savedLocation = localStorage.getItem("weatherLocation");

        if (savedLocation) {
          const location = JSON.parse(savedLocation);

          debugLog("Using saved location:", location);

          // Fetch weather for saved location
          const weatherResponse = await fetch(
            `https://api.open-meteo.com/v1/forecast?latitude=${location.lat}&longitude=${location.lon}&current_weather=true&temperature_unit=${weatherUnit()}`
          );

          if (weatherResponse.ok) {
            const weatherData = await weatherResponse.json();
            const weather = weatherData.current_weather;

            setWeatherData({
              temp: Math.round(weather.temperature),
              weatherCode: weather.weathercode,
              animation: getWeatherAnimation(
                weather.weathercode,
                isNightTime()
              ),
              description:
                weatherDescriptions[weather.weathercode] || "Unknown",
              city: location.city,
            });

            setForecast(await fetchForecast(location.lat, location.lon));

            return; // Exit early if saved location weather is loaded
          }
        }

        // Fallback to IP-based location
        let capturedTemp = null;
        let capturedIcon = null;
        let capturedType = null;
        let capturedCity = null;
        let capturedWeatherCode = null;

        const result = await fetchWeatherAndSetBackground({
          setTemperature: (t) => {
            debugLog("WeatherWidget: Temperature received:", t);
            capturedTemp = t;
          },
          setWeatherIcon: (i) => {
            debugLog("WeatherWidget: Icon received:", i);
            capturedIcon = i;
            // Try to extract weather code from icon class
            if (i) {
              if (i.includes("sun") && !i.includes("cloud"))
                capturedWeatherCode = 1;
              else if (i.includes("cloud-sun")) capturedWeatherCode = 2;
              else if (
                i.includes("cloud") &&
                !i.includes("rain") &&
                !i.includes("sun")
              )
                capturedWeatherCode = 3;
              else if (i.includes("smog")) capturedWeatherCode = 45;
              else if (i.includes("rain")) capturedWeatherCode = 61;
              else if (i.includes("snow")) capturedWeatherCode = 71;
              else if (i.includes("bolt")) capturedWeatherCode = 95;
            }
          },
          setWeatherType: (s) => {
            debugLog("WeatherWidget: Weather type received:", s);
            capturedType = s;
            // Extract city from the weather type string if it contains "in [city]"
            const cityMatch = s?.match(/in (.+)$/);

            if (cityMatch) {
              capturedCity = cityMatch[1];
            }
          },
          setIsUSA: () => {}, // Not needed for widget
          setBackgroundUrl: () => {}, // Not needed for widget
        });

        debugLog(
          "WeatherWidget: Result from fetchWeatherAndSetBackground:",
          result
        );
        debugLog("WeatherWidget: Captured values:", {
          temp: capturedTemp,
          icon: capturedIcon,
          type: capturedType,
          city: capturedCity,
          weatherCode: capturedWeatherCode,
        });

        // Extract just the weather condition from the type
        let weatherCondition = "Clear";

        if (capturedType) {
          // Remove "Currently " prefix and city suffix
          weatherCondition = capturedType
            .replace(/^Currently\s+/i, "")
            .replace(/\s+in\s+.+$/, "")
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
        } else if (result?.weather?.description) {
          weatherCondition = result.weather.description
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
        }

        // Use the captured values directly
        const data = {
          temp: capturedTemp || result?.weather?.tempDisplay || 72,
          weatherCode: capturedWeatherCode || 1,
          animation: getWeatherAnimation(
            capturedWeatherCode || 1,
            isNightTime()
          ),
          description: weatherCondition,
          city: capturedCity || result?.city || "Your Location",
        };

        debugLog("WeatherWidget: Setting weather data:", data);
        setWeatherData(data);

        if (result?.latitude != null && result?.longitude != null) {
          setForecast(await fetchForecast(result.latitude, result.longitude));
        }
      } catch (error) {
        console.error("WeatherWidget: Error fetching weather:", error);
        // Set default data if fetch fails
        setWeatherData({
          temp: 72,
          weatherCode: 1,
          animation: sunny,
          description: "Clear sky",
          city: "Your Location",
        });
      }
    };

    fetchWeather();
    const interval = setInterval(fetchWeather, 600000); // Update every 10 minutes
    const clockInterval = setInterval(() => {
      setClock(
        new Date().toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
      );
    }, 30000);

    return () => {
      clearInterval(interval);
      clearInterval(clockInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close the weather popup on Escape.
  useEffect(() => {
    if (!showWeatherPopup) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setShowWeatherPopup(false);
        setShowLocationForm(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showWeatherPopup]);

  const closeWeatherPopup = () => {
    setShowWeatherPopup(false);
    setShowLocationForm(false);
    setLocationInput("");
  };

  const handleLocationSubmit = async (e) => {
    e.preventDefault();
    if (!locationInput.trim()) return;

    try {
      const input = locationInput.trim().toUpperCase();
      let lat, lon, cityName;

      // Check if input is a US zip code (5 digits, optionally with +4 extension)
      const isUSZip = /^\d{5}(-\d{4})?$/.test(input);
      // Check if input is a UK postcode (various formats like E1 7QG, SW1A 1AA, etc.)
      const isUKPostcode = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i.test(input);
      // Check if input is a Canadian postal code (like K1A 0B1, A1V 0A0, T3J 2C2, etc.)
      // Canadian format: Letter-Number-Letter Space Number-Letter-Number
      // First letter can't be D, F, I, O, Q, U, W, Z
      // Second and third letters can't be D, F, I, O, Q, U
      const isCanadianPostal =
        /^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVWXYZ]\s?\d[ABCEGHJKLMNPRSTVWXYZ]\d$/i.test(
          input
        );
      // Check for other international postal codes (generic patterns)
      const isAustralianZip = /^\d{4}$/.test(input); // Australian postcodes are 4 digits
      const isJapaneseZip = /^\d{3}-?\d{4}$/.test(input); // Japanese format: 123-4567

      if (isUSZip) {
        // US Zip Code
        const geoResponse = await fetch(
          `https://api.zippopotam.us/us/${input}`
        );

        if (!geoResponse.ok) {
          throw new Error("Invalid US zip code");
        }

        const geoData = await geoResponse.json();

        lat = geoData.places[0].latitude;
        lon = geoData.places[0].longitude;
        cityName = `${geoData.places[0]["place name"]}, ${geoData.places[0]["state abbreviation"]}`;
      } else if (isUKPostcode) {
        // UK Postcode - format the postcode properly (add space if missing)
        let formattedPostcode = input;

        if (!input.includes(" ")) {
          // Add space before last 3 characters for UK postcodes
          formattedPostcode = `${input.slice(0, -3)} ${input.slice(-3)}`;
        }

        const geoResponse = await fetch(
          `https://api.zippopotam.us/gb/${encodeURIComponent(formattedPostcode)}`
        );

        if (!geoResponse.ok) {
          // Try postcodes.io as fallback for UK
          const postcodeResponse = await fetch(
            `https://api.postcodes.io/postcodes/${encodeURIComponent(formattedPostcode.replace(/\s/g, ""))}`
          );

          if (!postcodeResponse.ok) {
            throw new Error("Invalid UK postcode");
          }

          const postcodeData = await postcodeResponse.json();

          lat = postcodeData.result.latitude;
          lon = postcodeData.result.longitude;
          cityName = `${postcodeData.result.admin_ward || postcodeData.result.admin_district}, UK`;
        } else {
          const geoData = await geoResponse.json();

          lat = geoData.places[0].latitude;
          lon = geoData.places[0].longitude;
          cityName = `${geoData.places[0]["place name"]}, UK`;
        }
      } else if (isCanadianPostal) {
        // Canadian Postal Code - format properly
        debugLog("Processing Canadian postal code:", input);
        let formattedPostal = input.replace(/\s/g, "");

        // Insert space in the middle for Canadian format (A1A 1A1)
        if (formattedPostal.length === 6) {
          formattedPostal = `${formattedPostal.slice(0, 3)} ${formattedPostal.slice(3)}`;
        }
        debugLog("Formatted Canadian postal code:", formattedPostal);

        const geoResponse = await fetch(
          `https://api.zippopotam.us/ca/${encodeURIComponent(formattedPostal)}`
        );

        if (!geoResponse.ok) {
          // Fallback to Nominatim for Canadian postal codes not in Zippopotam
          debugLog("Zippopotam failed, trying Nominatim for:", formattedPostal);
          const nominatimResponse = await fetch(
            `https://nominatim.openstreetmap.org/search?postalcode=${encodeURIComponent(formattedPostal)}&country=canada&format=json&limit=1`
          );

          if (!nominatimResponse.ok) {
            throw new Error("Invalid Canadian postal code");
          }

          const nominatimData = await nominatimResponse.json();

          if (nominatimData.length === 0) {
            throw new Error("Canadian postal code not found");
          }

          ({ lat, lon } = nominatimData[0]);

          // Extract location info from display_name
          const parts = nominatimData[0].display_name.split(",");

          // Try to get a meaningful city name from the parts
          cityName =
            parts.length >= 2
              ? `${parts[0]}, ${parts[1].trim()}, Canada`
              : `${parts[0]}, Canada`;
        } else {
          const geoData = await geoResponse.json();

          lat = geoData.places[0].latitude;
          lon = geoData.places[0].longitude;
          cityName = `${geoData.places[0]["place name"]}, ${geoData.places[0]["state abbreviation"]}, Canada`;
        }
      } else if (isAustralianZip) {
        // Australian Postcode (4 digits)
        const geoResponse = await fetch(
          `https://api.zippopotam.us/au/${input}`
        );

        if (!geoResponse.ok) {
          throw new Error("Invalid Australian postcode");
        }

        const geoData = await geoResponse.json();

        lat = geoData.places[0].latitude;
        lon = geoData.places[0].longitude;
        cityName = `${geoData.places[0]["place name"]}, ${geoData.places[0]["state abbreviation"]}, Australia`;
      } else if (isJapaneseZip) {
        // Japanese Postal Code
        const formattedZip = input.replace(/-/g, "");
        const geoResponse = await fetch(
          `https://api.zippopotam.us/jp/${encodeURIComponent(formattedZip)}`
        );

        if (!geoResponse.ok) {
          throw new Error("Invalid Japanese postal code");
        }

        const geoData = await geoResponse.json();

        lat = geoData.places[0].latitude;
        lon = geoData.places[0].longitude;
        cityName = `${geoData.places[0]["place name"]}, Japan`;
      } else {
        // Try to geocode as a city name using Nominatim
        const geoResponse = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(input)}&format=json&limit=1`
        );

        if (!geoResponse.ok) {
          throw new Error("Failed to geocode location");
        }

        const geoData = await geoResponse.json();

        if (geoData.length === 0) {
          throw new Error("Location not found");
        }

        ({ lat, lon } = geoData[0]);

        // Extract city name from display_name
        const parts = geoData[0].display_name.split(",");

        cityName =
          parts.length >= 2
            ? `${parts[0]}, ${parts[parts.length - 1].trim()}`
            : parts[0];
      }

      // Fetch weather for the location
      const weatherResponse = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true&temperature_unit=${weatherUnit()}`
      );

      if (!weatherResponse.ok) {
        throw new Error("Failed to fetch weather");
      }

      const weatherData = await weatherResponse.json();
      const weather = weatherData.current_weather;

      // Update weather display
      setWeatherData({
        temp: Math.round(weather.temperature),
        weatherCode: weather.weathercode,
        animation: getWeatherAnimation(weather.weathercode, isNightTime()),
        description: weatherDescriptions[weather.weathercode] || "Unknown",
        city: cityName,
      });

      // Save the location preference
      localStorage.setItem(
        "weatherLocation",
        JSON.stringify({
          lat,
          lon,
          city: cityName,
        })
      );

      setForecast(await fetchForecast(lat, lon));

      setShowLocationForm(false);
      setLocationInput("");
    } catch (error) {
      console.error("Error updating location:", error);
      toast.error(
        `Failed to update location: ${error.message}. Please try a valid zip/postal code or city name.`
      );
    }
  };

  if (!weatherData || !weatherData.temp) {
    return <div className="text-white">Loading weather...</div>;
  }

  return (
    <>
      {/* Weather Widget */}
      <div
        className="flex cursor-pointer items-center gap-2 rounded-xl px-1 py-1 text-base text-white transition-transform hover:scale-105 lg:px-2.5 lg:py-1.5 lg:[background:linear-gradient(135deg,rgba(0,0,0,0.4)_0%,rgba(0,0,0,0.15)_100%)]"
        role="button"
        tabIndex={0}
        onClick={() => {
          setShowLocationForm(false);
          setShowWeatherPopup(true);
        }}
        onKeyDown={activateOnEnterOrSpace}
      >
        <div className={compact ? "w-7 h-7" : "w-9 h-9"}>
          <Lottie
            animationData={weatherData.animation}
            autoplay={true}
            loop={true}
            style={{ width: "100%", height: "100%" }}
          />
        </div>
        {compact ? (
          <span className="text-sm font-semibold max-lg:[text-shadow:0_1px_2px_rgba(0,0,0,0.45),0_2px_8px_rgba(0,0,0,0.35)] lg:drop-shadow-lg">
            {weatherData.temp}
            {weatherUnitSymbol()}
          </span>
        ) : (
          <>
            <span className="text-sm font-semibold max-lg:[text-shadow:0_1px_2px_rgba(0,0,0,0.45),0_2px_8px_rgba(0,0,0,0.35)] lg:drop-shadow-lg">
              {weatherData.temp}
              {weatherUnitSymbol()} | {weatherData.city} -{" "}
              {weatherData.description}
            </span>
            <span className="text-sm font-medium text-white/70 max-lg:[text-shadow:0_1px_2px_rgba(0,0,0,0.45),0_2px_8px_rgba(0,0,0,0.35)]">
              {clock}
            </span>
          </>
        )}
      </div>

      {/* Weather overlay: iOS glass forecast card + location dialog */}
      <AnimatePresence>
        {showWeatherPopup && (
          <motion.div
            animate={{ opacity: 1 }}
            className="weather-overlay"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            role="presentation"
            transition={{ duration: 0.2, ease: "easeInOut" }}
            onClick={(e) => {
              if (e.target === e.currentTarget) closeWeatherPopup();
            }}
          >
            {showLocationForm ? (
              <form
                aria-labelledby="weather-location-title"
                aria-modal="true"
                className="lunova-glass weather-dialog"
                role="dialog"
                onSubmit={handleLocationSubmit}
              >
                <div>
                  <h2 id="weather-location-title">Weather Location</h2>
                  <p>Enter a city name or US zip code</p>
                </div>
                <input
                  autoFocus
                  aria-label="City name or US zip code"
                  autoComplete="off"
                  className="weather-field"
                  placeholder="New York or 10001"
                  type="text"
                  value={locationInput}
                  onChange={(e) => setLocationInput(e.target.value)}
                />
                <div className="weather-dialog-actions">
                  <button
                    className="lunova-glass weather-pill"
                    type="button"
                    onClick={() => {
                      setShowLocationForm(false);
                      setLocationInput("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="lunova-glass weather-pill is-primary"
                    disabled={!locationInput.trim()}
                    type="submit"
                  >
                    Update
                  </button>
                </div>
              </form>
            ) : (
              <div
                aria-label="Weather forecast"
                aria-modal="true"
                className="lunova-glass weather-card"
                role="dialog"
              >
                <div className="weather-header">
                  <div className="weather-header-icon">
                    <Lottie
                      animationData={weatherData.animation}
                      autoplay={true}
                      loop={true}
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                  <span>
                    {weatherData.temp}
                    {weatherUnitSymbol()} | {weatherData.city} -{" "}
                    {weatherData.description}
                  </span>
                </div>

                <div className="weather-divider" />

                {forecast.length > 0 ? (
                  <div className="weather-days">
                    {forecast.map((day) => (
                      <div className="weather-day" key={day.date}>
                        <span className="weather-day-label">{day.label}</span>
                        <span className="weather-day-date">{day.date}</span>
                        <div className="weather-day-icon">
                          <Lottie
                            animationData={day.animation}
                            autoplay={true}
                            loop={true}
                            style={{ width: "100%", height: "100%" }}
                          />
                        </div>
                        <span className="weather-day-condition">
                          {day.condition}
                        </span>
                        <span className="weather-day-temp">
                          {day.lo}–{day.hi}
                          {weatherUnitSymbol()}
                        </span>
                        {day.aqi != null && (
                          <span className="weather-day-aqi">AQI {day.aqi}</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="weather-empty">
                    Forecast unavailable right now.
                  </div>
                )}

                <div className="weather-divider" />

                <div className="weather-actions">
                  <button
                    autoFocus
                    className="weather-update"
                    type="button"
                    onClick={() => setShowLocationForm(true)}
                  >
                    Update Location
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default React.memo(WeatherWidget);
